import React, { useEffect, useState } from 'react';
import { useProgress } from '../hooks/useProgress';
import { useWeddingDay } from '../state/useWeddingDay';

const e = React.createElement;

// Read off the payload the page already loaded, so the card costs no request.
const remainingFrom = (locksAt) => {
  const at = locksAt ? new Date(locksAt).getTime() : 0;
  if (!at || Number.isNaN(at)) return 0;
  return Math.max(0, at - Date.now());
};

const parts = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  return {
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  };
};

const pad = (n) => String(n).padStart(2, '0');

// Minutes are the finest unit here: the card is read once, not watched.
const longForm = ({ days, hours, minutes, seconds }) => {
  if (days > 0) return `${days}d ${pad(hours)}h ${pad(minutes)}m`;
  if (hours > 0) return `${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
  return `${pad(minutes)}m ${pad(seconds)}s`;
};

const shortForm = ({ days, hours, minutes }) => {
  if (days > 0) return `${days}d`;
  if (hours > 0) return `${hours}h`;
  return `${minutes}m`;
};

/**
 * Whose wedding this is, and how long they have left on it.
 *
 * The same card the creative preferences form carries, counting down the
 * same way: on a phone the header has no room for the booking name, and it
 * is the first thing a couple needs to know they are in the right form.
 * A form with nothing left to count still has to name the booking, so it
 * falls back to how far through the sections they are.
 */
const BriefCard = () => {
  const { bookingName, locksAt, closed } = useWeddingDay();
  const { doneCount, countedTotal } = useProgress();
  const [remaining, setRemaining] = useState(() => remainingFrom(locksAt));

  useEffect(() => {
    if (!locksAt || remainingFrom(locksAt) <= 0) {
      setRemaining(0);
      return undefined;
    }
    // Reads the clock each tick rather than decrementing, so a backgrounded
    // tab catches up instead of drifting.
    const timer = setInterval(() => {
      const left = remainingFrom(locksAt);
      setRemaining(left);
      if (left <= 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [locksAt]);

  if (!bookingName) return null;

  const counting = !!locksAt && !closed && remaining > 0;
  const split = parts(remaining);

  return e('div', { className: 'wd-dcard' }, [
    e('span', { className: 'wd-dcard-chip', key: 'chip' },
      counting ? shortForm(split) : `${doneCount}/${countedTotal}`),
    e('span', { className: 'wd-dcard-text', key: 'text' }, [
      e('span', { className: 'wd-dcard-name', key: 'name' }, bookingName),
      e('span', { className: 'wd-dcard-meta', key: 'meta' },
        counting
          ? `Form closes in ${longForm(split)}`
          : `${doneCount} of ${countedTotal} sections done`),
    ]),
  ]);
};

export default BriefCard;
